import { v4 as uuid } from 'uuid';
import React, { useCallback, useMemo } from 'react';
import { bindPopover, usePopupState } from 'material-ui-popup-state/hooks';

import { usePaginationParams } from '@occc-app/hooks/usePaginationParams';
import {
  ScanDataFileStatusType,
  ScanDataSubmissionFilter,
  ScanDataSubmissionsFragment,
} from '@occc/graphql';
import { isFiltersSelected } from '@occc-app/design-system';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useAdvancedQueryParam } from '@occc/utils';
import { formatDateTime, DateTimeFormat } from '@occc-app/lib/date';

import { useGetScanDataSubmissions } from './past-submissions.query.hook';

import {
  PAST_SUBMISSIONS_DEFAULT_STATUS,
  defaultStatusQueryParams,
  defaultDateQueryParams,
  defaultPaginationParams,
} from '../constants/past-submissions-table.constants';
import { PastSubmission, PastSubmissionFilterFormValues } from '../scan-data-weekly-file.types';

export const usePastSubmissionsTableController = () => {
  const [fileStatusEquals, setFileStatusFilter] = useAdvancedQueryParam<
    ScanDataFileStatusType | 'all'
  >(defaultStatusQueryParams);
  const [dateFilter, setDateFilter] = useAdvancedQueryParam<Date | undefined>(
    defaultDateQueryParams,
  );
  const popupState = usePopupState({ variant: 'popover', popupId: 'filters' });
  const popoverProps = React.useMemo(() => {
    return {
      ...bindPopover({
        ...popupState,
      }),
    };
  }, [popupState]);

  const onFilterFormSubmit = useCallback(
    (values: PastSubmissionFilterFormValues) => {
      setFileStatusFilter(values.status);
      setDateFilter(values.date);

      popoverProps.onClose();
    },
    [popoverProps, setFileStatusFilter, setDateFilter],
  );

  const onFilterFormReset = useCallback(() => {
    setFileStatusFilter(PAST_SUBMISSIONS_DEFAULT_STATUS);
    setDateFilter(undefined);

    popoverProps.onClose();
  }, [popoverProps, setFileStatusFilter, setDateFilter]);
  const isFilterSelected = isFiltersSelected([fileStatusEquals], [PAST_SUBMISSIONS_DEFAULT_STATUS]);

  const [paginationInfo, onPageChange] = usePaginationParams(defaultPaginationParams);

  const queryFilter: ScanDataSubmissionFilter = useMemo((): ScanDataSubmissionFilter => {
    return {
      fileStatusEquals: fileStatusEquals === 'all' ? undefined : fileStatusEquals,
      submissionDateEquals: dateFilter ? formatDateTime(dateFilter, DateTimeFormat.DateDashed) : '',
    };
  }, [fileStatusEquals, dateFilter]);

  const {
    loading,
    error,
    data: rawData,
    paginationProps,
  } = useGetScanDataSubmissions({
    filter: queryFilter,
    paginationInfo,
    onPageChange,
  });

  const items: PastSubmission[] = useMemo(
    () =>
      rawData?.map(
        (item: ScanDataSubmissionsFragment): PastSubmission => ({
          uuid: uuid(),
          submissionDate: item.submissionDate,
          type: item.type,
          transactionsReported: item.transactionsReportedCount,
          transactionsReportedChangePercent: item.transactionsReportedChangePercent,
          submissionGroups: {
            total: item.submissionGroupsCount,
            submitted: item.submittedSubmissionGroupsCount,
          },
          fileStatus: item.fileStatus,
          estimatedReimbursement: item.estimatedReimbursements,
          submissions: [],
        }),
      ) || [],
    [rawData],
  );

  return useMemo(
    () => ({
      paginationProps,
      loading,
      error,
      formFilter: {
        status: fileStatusEquals,
        date: dateFilter,
      },
      popupState,
      onFilterFormSubmit,
      popoverProps,
      onFilterFormReset,
      isFilterSelected,
      items,
    }),
    [
      paginationProps,
      loading,
      error,
      fileStatusEquals,
      dateFilter,
      popupState,
      onFilterFormSubmit,
      popoverProps,
      onFilterFormReset,
      isFilterSelected,
      items,
    ],
  );
};
